import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import type {
  AiSuggestion,
  ApiClient,
  ConversationSearchFilters,
  ConversationSearchResult,
  ConversationTimeline,
  CustomerPatch,
  FollowUpTask,
  LoanFactCandidate,
  LoanOrder,
  PublicUser,
  SafeWhatsAppSession,
  SuggestionFeedbackAction
} from "../api/client.js";
import type { Role } from "@scs/contracts";
import { AiPanel } from "../components/ai-panel.js";
import { ConversationList } from "../components/conversation-list.js";
import { CustomerOrderPanel } from "../components/customer-order-panel.js";
import { FollowUpPanel } from "../components/follow-up-panel.js";
import { MessageTimeline } from "../components/message-timeline.js";
import { MessageComposer } from "../components/message-composer.js";
import { subscribeToRealtime } from "../realtime-client.js";

export type WorkbenchSelection = {
  candidates?: LoanFactCandidate[];
  followUps: FollowUpTask[];
  orders: LoanOrder[];
  suggestions: AiSuggestion[];
  timeline: ConversationTimeline;
};

export function ConversationsPage(props: {
  api: ApiClient;
  initialConversations?: readonly ConversationSearchResult[];
  initialSelection?: WorkbenchSelection | null;
  currentUserId?: string;
  initialSessions?: readonly SafeWhatsAppSession[];
  role: Role;
  sessionToken: string;
}): React.JSX.Element {
  const [conversations, setConversations] = useState<ConversationSearchResult[]>([
    ...(props.initialConversations ?? [])
  ]);
  const [listLoading, setListLoading] = useState(props.initialConversations === undefined);
  const [selection, setSelection] = useState<WorkbenchSelection | null>(
    props.initialSelection ?? null
  );
  const [compactView, setCompactView] = useState<"list" | "chat" | "details">(
    props.initialSelection === null || props.initialSelection === undefined ? "list" : "chat"
  );
  const [customerName, setCustomerName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [loanNumber, setLoanNumber] = useState("");
  const [collectorId, setCollectorId] = useState("");
  const [daysPastDueMin, setDaysPastDueMin] = useState("");
  const [daysPastDueMax, setDaysPastDueMax] = useState("");
  const [intent, setIntent] = useState("");
  const [sessionStatus, setSessionStatus] = useState("");
  const [followUpDueFrom, setFollowUpDueFrom] = useState("");
  const [followUpDueTo, setFollowUpDueTo] = useState("");
  const [riskLevel, setRiskLevel] = useState<"" | "HIGH" | "LOW" | "MEDIUM" | "UNKNOWN">("");
  const [error, setError] = useState<string | null>(null);
  const [composerText, setComposerText] = useState("");
  const [activeFilters, setActiveFilters] = useState<ConversationSearchFilters>({});
  const [collectors, setCollectors] = useState<PublicUser[]>([]);
  const [activeInspector, setActiveInspector] = useState<"customer" | "order" | "ai" | "followUp">("customer");
  const [sessions, setSessions] = useState<SafeWhatsAppSession[]>([...(props.initialSessions ?? [])]);
  const [coreAvailable, setCoreAvailable] = useState(false);
  const selectedConversationId = useRef(selection?.timeline.conversation.id);
  const timelineCursor = useRef(selection?.timeline.cursor ?? null);
  const selectionRequest = useRef(0);
  selectedConversationId.current = selection?.timeline.conversation.id;
  const canReassign = props.role === "ENTERPRISE_ADMIN" || props.role === "SUPERVISOR";

  async function search(filters: ConversationSearchFilters = activeFilters): Promise<void> {
    try {
      const results = await props.api.searchConversations(filters);
      setConversations(results);
      setActiveFilters(filters);
      setError(null);
    } catch {
      setError("搜索会话失败。");
    }
  }

  useEffect(() => {
    if (!canReassign) return;
    void props.api.listUsers().then(
      (users) => { setCollectors(users.filter((user) => user.role === "COLLECTOR" && user.status === "ACTIVE")); },
      () => { setCollectors([]); }
    );
  }, [canReassign, props.api]);

  useEffect(
    () => {
      let disposed = false;
      let refreshing = false;
      let refreshAgain = false;
      let notificationTimer: ReturnType<typeof setTimeout> | undefined;
      const refresh = async (): Promise<void> => {
        if (disposed) return;
        if (refreshing) { refreshAgain = true; return; }
        refreshing = true;
        const id = selectedConversationId.current;
        const ready = await props.api.coreAvailable();
        if (!disposed) setCoreAvailable(ready);
        try {
          await Promise.all([
            props.api.searchConversations(activeFilters).then((results) => {
              if (!disposed) { setConversations(results); setListLoading(false); }
            }, () => {
              if (!disposed) setListLoading(false);
            }),
            props.api.listWhatsAppSessions().then((results) => {
              if (!disposed) setSessions(results);
            }, () => { if (!disposed) setSessions([]); }),
            id === undefined ? Promise.resolve() : Promise.all([
              props.api.getConversationTimeline(id, timelineCursor.current),
              props.api.listSuggestions(id),
              props.api.listLoanFactCandidates(id)
            ]).then(([timeline, suggestions, candidates]) => {
              if (!disposed) {
                timelineCursor.current = timeline.cursor ?? timelineCursor.current;
                setSelection((current) => current?.timeline.conversation.id === id
                  ? { ...current, timeline: mergeTimeline(current.timeline, timeline), suggestions, candidates } : current);
              }
              if (!disposed && document.visibilityState === "visible") {
                void props.api.markConversationRead(id).then(() => {
                  if (!disposed) setConversations((current) => current.map((item) => item.conversationId === id ? { ...item, unreadCount: 0 } : item));
                }, () => undefined);
              }
            })
          ]);
        } catch {
          // The next poll also repairs a missed/reconnecting WebSocket event.
        } finally {
          refreshing = false;
          if (refreshAgain && !disposed) { refreshAgain = false; void refresh(); }
        }
      };
      void refresh();
      const unsubscribe = subscribeToRealtime(() => {
        // Every authorized event refreshes the list, including a new customer
        // while no chat is open. Coalesce bursts from history synchronization.
        if (notificationTimer !== undefined) clearTimeout(notificationTimer);
        notificationTimer = setTimeout(() => { void refresh(); }, 100);
      }, { sessionToken: props.sessionToken });
      const timer = setInterval(() => { void refresh(); }, 5_000);
      const onVisible = (): void => { if (document.visibilityState === "visible") void refresh(); };
      document.addEventListener("visibilitychange", onVisible);
      return () => {
        disposed = true;
        unsubscribe();
        clearInterval(timer);
        if (notificationTimer !== undefined) clearTimeout(notificationTimer);
        document.removeEventListener("visibilitychange", onVisible);
      };
    },
    [activeFilters, props.api, props.sessionToken]
  );

  async function submitSearch(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    await search({
      ...(customerName === "" ? {} : { customerName }),
      ...(phoneNumber === "" ? {} : { phoneNumber }),
      ...(loanNumber === "" ? {} : { loanNumber }),
      ...(collectorId === "" ? {} : { collectorId }),
      ...(daysPastDueMin === "" ? {} : { daysPastDueMin: Number(daysPastDueMin) }),
      ...(daysPastDueMax === "" ? {} : { daysPastDueMax: Number(daysPastDueMax) }),
      ...(intent === "" ? {} : { intent }),
      ...(riskLevel === "" ? {} : { riskLevel }),
      ...(sessionStatus === "" ? {} : { sessionStatus: sessionStatus as NonNullable<ConversationSearchFilters["sessionStatus"]> }),
      ...(followUpDueFrom === "" ? {} : { followUpDueFrom: new Date(followUpDueFrom).toISOString() }),
      ...(followUpDueTo === "" ? {} : { followUpDueTo: new Date(followUpDueTo).toISOString() })
    });
  }

  async function select(item: ConversationSearchResult): Promise<void> {
    if (item.conversationId === null) return;
    const request = ++selectionRequest.current;
    timelineCursor.current = null;
    try {
      const [timeline, orders, suggestions, allFollowUps, candidates] = await Promise.all([
        props.api.getConversationTimeline(item.conversationId),
        props.api.listOrders(item.customer.id),
        props.api.listSuggestions(item.conversationId),
        props.api.listFollowUpTasks(),
        props.api.listLoanFactCandidates(item.conversationId)
      ]);
      if (request !== selectionRequest.current) return;
      setSelection({
        followUps: allFollowUps.filter((task) => task.customerId === item.customer.id),
        orders,
        candidates,
        suggestions,
        timeline
      });
      setCompactView("chat");
      timelineCursor.current = timeline.cursor ?? null;
      void props.api.markConversationRead(item.conversationId).then(() => {
        if (request === selectionRequest.current) setConversations((current) => current.map((entry) => entry.conversationId === item.conversationId ? { ...entry, unreadCount: 0 } : entry));
      }, () => undefined);
      setComposerText("");
      setError(null);
    } catch {
      if (request === selectionRequest.current) setError("加载会话工作台失败。");
    }
  }

  async function recordAction(
    suggestionId: string,
    action: SuggestionFeedbackAction
  ): Promise<void> {
    const updated = await props.api.recordSuggestionAction(suggestionId, action);
    setSelection((current) => current === null ? null : {
      ...current,
      suggestions: current.suggestions.map((item) => item.id === updated.id ? updated : item)
    });
  }

  async function createFollowUp(input: { dueAt: string; title: string }): Promise<void> {
    if (selection === null) return;
    const task = await props.api.createFollowUpTask(selection.timeline.customer.id, input);
    setSelection((current) => current === null ? null : {
      ...current,
      followUps: [...current.followUps, task].sort((left, right) => left.dueAt.localeCompare(right.dueAt))
    });
  }

  async function updateFollowUp(
    id: string,
    patch: { dueAt?: string; status?: FollowUpTask["status"]; title?: string }
  ): Promise<void> {
    const task = await props.api.updateFollowUpTask(id, patch);
    setSelection((current) => current === null ? null : {
      ...current,
      followUps: current.followUps.map((item) => item.id === task.id ? task : item)
    });
  }

  async function createOrder(facts: Parameters<ApiClient["createOrder"]>[1]): Promise<void> {
    if (selection === null) return;
    const order = await props.api.createOrder(selection.timeline.customer.id, facts);
    setSelection((current) => current === null ? null : {
      ...current,
      orders: [...current.orders, order]
    });
  }

  async function reviewLoanCandidate(candidateId: string, action: "CONFIRM" | "REJECT", reason?: string): Promise<void> {
    const candidate = selection?.candidates?.find((item) => item.id === candidateId);
    if (candidate === undefined) return;
    const updated = await props.api.reviewLoanFactCandidate({
      action,
      candidateId,
      expectedUpdatedAt: candidate.updatedAt,
      ...(reason === undefined ? {} : { reason })
    });
    setSelection((current) => current === null ? null : {
      ...current,
      candidates: (current.candidates ?? []).map((item) => item.id === updated.id ? updated : item)
    });
  }

  async function updateOrder(
    orderId: string,
    facts: Parameters<ApiClient["updateOrder"]>[1]
  ): Promise<void> {
    const order = await props.api.updateOrder(orderId, facts);
    setSelection((current) => current === null ? null : {
      ...current,
      orders: current.orders.map((item) => item.id === order.id ? order : item)
    });
  }

  async function updateCustomer(
    customerId: string,
    patch: CustomerPatch
  ): Promise<void> {
    const customer = await props.api.updateCustomer(customerId, patch);
    setConversations((current) => current.map((item) =>
      item.customer.id === customer.id ? { ...item, customer } : item
    ));
    setSelection((current) => current === null ? null : {
      ...current,
      timeline: { ...current.timeline, customer }
    });
  }

  async function reassignCustomer(customerId: string, collectorId: string): Promise<void> {
    const result = await props.api.reassignCustomer(customerId, collectorId);
    const customer = result.customer;
    setConversations((current) => current.map((item) =>
      item.customer.id === customer.id ? { ...item, customer } : item
    ));
    setSelection((current) => current === null ? null : {
      ...current,
      timeline: { ...current.timeline, customer }
    });
  }

  async function refreshTimeline(): Promise<void> {
    if (selection === null) return;
    const id = selection.timeline.conversation.id;
    const timeline = await props.api.getConversationTimeline(id, timelineCursor.current);
    timelineCursor.current = timeline.cursor ?? timelineCursor.current;
    setSelection((current) => current?.timeline.conversation.id === id
      ? { ...current, timeline: mergeTimeline(current.timeline, timeline) } : current);
    await search();
  }

  const canAct = props.role !== "AUDITOR";
  return (
    <main className="wa-app">
      <form aria-label="会话搜索" className="wa-filters" onSubmit={(event) => { void submitSearch(event); }}>
        <label>客户名<input onChange={(event) => { setCustomerName(event.currentTarget.value); }} value={customerName} /></label>
        <label>手机号<input onChange={(event) => { setPhoneNumber(event.currentTarget.value); }} value={phoneNumber} /></label>
        <label>贷款号<input onChange={(event) => { setLoanNumber(event.currentTarget.value); }} value={loanNumber} /></label>
        <label>催收员 ID<input onChange={(event) => { setCollectorId(event.currentTarget.value); }} value={collectorId} /></label>
        <label>最少逾期天数<input min="0" onChange={(event) => { setDaysPastDueMin(event.currentTarget.value); }} type="number" value={daysPastDueMin} /></label>
        <label>最多逾期天数<input min="0" onChange={(event) => { setDaysPastDueMax(event.currentTarget.value); }} type="number" value={daysPastDueMax} /></label>
        <label>客户意图<input onChange={(event) => { setIntent(event.currentTarget.value); }} value={intent} /></label>
        <label>
          风险
          <select onChange={(event) => { setRiskLevel(event.currentTarget.value as typeof riskLevel); }} value={riskLevel}>
            <option value="">全部</option>
            <option>LOW</option><option>MEDIUM</option><option>HIGH</option><option>UNKNOWN</option>
          </select>
        </label>
        <label>
          Session 状态
          <select onChange={(event) => { setSessionStatus(event.currentTarget.value); }} value={sessionStatus}>
            <option value="">全部</option>
            <option>CREATED</option><option>QR_READY</option><option>AUTHENTICATING</option>
            <option>CONNECTED</option><option>DISCONNECTED</option><option>AUTH_FAILURE</option>
            <option>BANNED_OR_UNAVAILABLE</option><option>ARCHIVED</option>
          </select>
        </label>
        <label>待跟进起始<input onChange={(event) => { setFollowUpDueFrom(event.currentTarget.value); }} type="datetime-local" value={followUpDueFrom} /></label>
        <label>待跟进截止<input onChange={(event) => { setFollowUpDueTo(event.currentTarget.value); }} type="datetime-local" value={followUpDueTo} /></label>
        <button type="submit">搜索</button>
      </form>
      {error === null ? null : <p role="alert">{error}</p>}
      {!coreAvailable ? <p aria-live="polite" role="status">核心业务服务暂不可用，已暂停新的 Web 发送；正在等待恢复。</p> : null}
      <div aria-label="三栏工作台" className="workbench wa-workbench" data-compact-view={compactView}>
        <ConversationList
          conversations={conversations}
          loading={listLoading}
          session={sessions.find((session) => session.collectorId === props.currentUserId && session.status !== "ARCHIVED") ?? null}
          onSelect={(item) => { void select(item); }}
          selectedCustomerId={selection?.timeline.customer.id ?? null}
        />
        {selection === null ? (
          <section aria-label="消息时间线" className="wa-chat-empty"><span>💬</span><h2>选择一个会话</h2><p>从左侧选择客户，开始查看并处理 WhatsApp 对话。</p></section>
        ) : <section aria-label="消息工作区" className="message-workspace wa-chat">
          <GatewayRuntimeNotice session={sessions.find((session) => session.collectorId === selection.timeline.customer.assignedCollectorId && session.status !== "ARCHIVED") ?? null} />
          <MessageTimeline
            api={props.api}
            conversationId={selection.timeline.conversation.id}
            customer={selection.timeline.customer}
            messages={selection.timeline.messages}
            outboundRequests={selection.timeline.outboundRequests}
            onMobileBack={() => { setCompactView("list"); }}
            onMobileDetails={() => { setCompactView("details"); }}
          />
          <AiDraftPreview
            canAct={canAct}
            onUse={(text) => { setComposerText(text); }}
            suggestions={selection.suggestions}
          />
          <MessageComposer
            api={props.api}
            canAct={canAct}
            coreAvailable={coreAvailable}
            conversationId={selection.timeline.conversation.id}
            customer={selection.timeline.customer}
            hasConnectedSession={sessions.some((session) => session.collectorId === selection.timeline.customer.assignedCollectorId && session.status === "CONNECTED" && session.sendAllowed)}
            key={selection.timeline.conversation.id}
            onSent={refreshTimeline}
            onTextChange={setComposerText}
            text={composerText}
            syntheticSmokeMode={import.meta.env.VITE_SCS_SMOKE_TEST_MODE === "true"}
          />
        </section>}
        <aside aria-label="客户订单 AI 与任务">
          {selection === null ? <div className="wa-inspector-empty"><span aria-hidden="true">☷</span><strong>会话详情</strong><p>选择左侧会话后，可在这里查看客户资料、订单和跟进任务。</p></div> : (
            <>
              <button className="wa-mobile-back" onClick={() => { setCompactView("chat"); }} type="button">
                <span aria-hidden="true">‹</span> 返回对话
              </button>
              <div aria-label="客户上下文标签" className="workbench-tabs" role="tablist">
                <InspectorTab active={activeInspector === "customer"} id="customer" label="客户资料" onSelect={setActiveInspector} />
                <InspectorTab active={activeInspector === "order"} id="order" label="贷款订单" onSelect={setActiveInspector} />
                <InspectorTab active={activeInspector === "ai"} id="ai" label="AI 建议" onSelect={setActiveInspector} />
                <InspectorTab active={activeInspector === "followUp"} id="followUp" label="跟进任务" onSelect={setActiveInspector} />
              </div>
              {activeInspector === "customer" ? <InspectorPanel id="customer" key={selection.timeline.conversation.id}><CustomerOrderPanel
                canEdit={canAct}
                canManageRestrictedFields={canReassign}
                canReassign={canReassign}
                collectors={collectors}
                customer={selection.timeline.customer}
                getHistory={props.api.listCustomerAssignments.bind(props.api)}
                onCreate={createOrder}
                onReassign={reassignCustomer}
                onUpdateCustomer={updateCustomer}
                onUpdate={updateOrder}
                orders={[]}
                showOrders={false}
              /></InspectorPanel> : null}
              {activeInspector === "order" ? <InspectorPanel id="order" key={selection.timeline.conversation.id}><CustomerOrderPanel
                canEdit={canAct}
                canManageRestrictedFields={canReassign}
                canReassign={false}
                collectors={collectors}
                customer={selection.timeline.customer}
                getHistory={props.api.listCustomerAssignments.bind(props.api)}
                onCreate={createOrder}
                candidates={selection.candidates ?? []}
                onReviewCandidate={reviewLoanCandidate}
                onReassign={reassignCustomer}
                onUpdateCustomer={updateCustomer}
                onUpdate={updateOrder}
                orders={selection.orders}
                showCustomer={false}
              /></InspectorPanel> : null}
              {activeInspector === "ai" ? <InspectorPanel id="ai"><AiPanel
                canAct={canAct}
                hasValidOrder={selection.orders.some((order) => order.aiFactsEligible)}
                onAction={recordAction}
                onUseInComposer={setComposerText}
                suggestions={selection.suggestions}
              /></InspectorPanel> : null}
              {activeInspector === "followUp" ? <InspectorPanel id="followUp"><FollowUpPanel
                canEdit={canAct}
                onCreate={createFollowUp}
                onUpdate={updateFollowUp}
                tasks={selection.followUps}
              /></InspectorPanel> : null}
            </>
          )}
        </aside>
      </div>
    </main>
  );
}

function mergeTimeline(previous: ConversationTimeline, incoming: ConversationTimeline): ConversationTimeline {
  const byId = new Map(previous.messages.map((message) => [message.id, message]));
  for (const message of incoming.messages) byId.set(message.id, message);
  const messages = [...byId.values()].sort((left, right) => left.occurredAt.localeCompare(right.occurredAt) || left.id.localeCompare(right.id));
  return { ...incoming, cursor: incoming.cursor ?? previous.cursor ?? null, messages };
}

function GatewayRuntimeNotice(props: { session: SafeWhatsAppSession | null }): React.JSX.Element | null {
  if (props.session?.channelOwner !== "GATEWAY") return null;
  const runtime = props.session.gatewayRuntime;
  if (runtime === undefined) return <p aria-live="polite" className="wa-gateway-notice" role="status">独立网关尚未报告状态；消息可能仍在本地等待同步。</p>;
  if (runtime.state === "AUTH_FAILURE" || runtime.state === "DISCONNECTED" || runtime.state === "AUTHENTICATING") {
    return <p aria-live="polite" className="wa-gateway-notice" role="status">网关状态：{runtime.state}；不能确认消息已正常同步。待同步事件 {runtime.pendingEventCount} 条（截至 {runtime.reportedAt}）。</p>;
  }
  if (runtime.state === "STOPPED" || runtime.state === "STALE") {
    return <p aria-live="polite" className="wa-gateway-notice" role="status">网关{runtime.state === "STOPPED" ? "已停止" : "状态报告已过期"}；待同步事件 {runtime.pendingEventCount} 条（截至 {runtime.reportedAt}）。</p>;
  }
  return runtime.pendingEventCount > 0
    ? <p aria-live="polite" className="wa-gateway-notice" role="status">网关在线；有 {runtime.pendingEventCount} 条事件等待核心同步。</p>
    : null;
}

function InspectorTab(props: { active: boolean; id: "customer" | "order" | "ai" | "followUp"; label: string; onSelect: (id: "customer" | "order" | "ai" | "followUp") => void }): React.JSX.Element {
  return <button aria-controls={`inspector-${props.id}`} aria-selected={props.active} id={`inspector-tab-${props.id}`} onClick={() => { props.onSelect(props.id); }} role="tab" type="button">{props.label}</button>;
}

function InspectorPanel(props: { children: React.ReactNode; id: "customer" | "order" | "ai" | "followUp" }): React.JSX.Element {
  return <div aria-labelledby={`inspector-tab-${props.id}`} className="wa-inspector-panel" id={`inspector-${props.id}`} role="tabpanel">{props.children}</div>;
}

function AiDraftPreview(props: { canAct: boolean; onUse: (text: string) => void; suggestions: readonly AiSuggestion[] }): React.JSX.Element | null {
  const suggestion = props.suggestions.find((item) => item.isCurrent !== false);
  if (suggestion === undefined) return null;
  return <section aria-label="AI 草稿预览" className="ai-draft-preview">
    <div><strong>AI 草稿</strong><p>{suggestion.originalText}</p></div>
    {props.canAct ? <button onClick={() => { props.onUse(suggestion.originalText); }} type="button">写入消息框</button> : null}
  </section>;
}
